/*
 * Brinde automático Beout — V11.
 *
 * Implementação baseada no mesmo ciclo do medidor do tema:
 * - consulta o carrinho real em /cart.js;
 * - adiciona o item por /cart/add.js com propriedades privadas;
 * - normaliza e remove linhas por chave em /cart/update.js;
 * - solicita e aplica as seções AJAX somente quando o carrinho realmente muda;
 * - não usa MutationObserver, intervalos contínuos ou índices de linha para o brinde.
 */
(function () {
  'use strict';

  var CONFIG_ID = 'BeoutAutomaticGiftConfig';
  var ROLE_KEY = '_beout_gift_role';
  var ROLE_VALUE = 'automatic_gift';
  var VARIANT_KEY = '_beout_gift_variant_id';
  var BADGE_KEY = '_beout_gift_badge_text';
  var SOURCE_KEY = '_beout_gift_source';
  var SOURCE_VALUE = 'theme_campaign_v11';
  var MAX_PASSES = 4;

  function toNumber(value, fallback) {
    var number = Number(value);
    return Number.isFinite(number) ? number : (fallback || 0);
  }

  function parseMoneyToCents(value) {
    var raw = String(value == null ? '' : value).trim().replace(/\s+/g, '');
    if (!raw) return 0;

    var comma = raw.lastIndexOf(',');
    var dot = raw.lastIndexOf('.');
    var normalized = raw;

    if (comma > -1 && dot > -1) {
      normalized = comma > dot
        ? normalized.replace(/\./g, '').replace(',', '.')
        : normalized.replace(/,/g, '');
    } else if (comma > -1) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    }

    normalized = normalized.replace(/[^0-9.-]/g, '');
    var parsed = Number(normalized);
    return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed * 100)) : 0;
  }

  function parseCartState(value) {
    if (!value) return null;
    if (typeof value !== 'string') return value;

    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }

  function readConfig() {
    var node = document.getElementById(CONFIG_ID);
    if (!node) return null;

    try {
      var data = JSON.parse(node.textContent || '{}');
      return {
        enabled: data.enabled === true,
        productId: Math.max(0, toNumber(data.productId)),
        variantId: Math.max(0, toNumber(data.variantId)),
        minimumCents: parseMoneyToCents(data.minimumValueRaw),
        badgeText: String(data.badgeText || 'Brinde').trim() || 'Brinde',
        visualFree: data.visualFree === true
      };
    } catch (error) {
      console.error('[Store Experts][Brinde] Configuração inválida.', error);
      return null;
    }
  }

  function rootUrl() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    return String(root).replace(/\/?$/, '/');
  }

  function route(name) {
    if (window.theme && window.theme.routes) {
      if (name === 'add' && window.theme.routes.cart_add_url) return window.theme.routes.cart_add_url;
      if (name === 'update' && window.theme.routes.cart_update_url) return window.theme.routes.cart_update_url;
    }

    if (name === 'add') return rootUrl() + 'cart/add.js';
    if (name === 'update') return rootUrl() + 'cart/update.js';
    return rootUrl() + 'cart.js';
  }

  function fetchJson(url, options) {
    return fetch(url, options).then(function (response) {
      return response.json().then(function (payload) {
        if (!response.ok || (payload && payload.status)) {
          var error = new Error(
            (payload && (payload.description || payload.message)) ||
            'Não foi possível atualizar o carrinho.'
          );
          error.status = (payload && payload.status) || response.status;
          throw error;
        }
        return payload;
      });
    });
  }

  var manager = {
    config: null,
    isSyncing: false,
    syncRequested: false,
    pendingTimer: null,
    currentPromise: null,
    lastCart: null,
    lastErrorSignature: '',
    pendingExpectedVariantId: 0,
    isRenderingGiftChange: false,
    miniCartRefreshPromise: null,

    refreshConfig: function () {
      this.config = readConfig();
      return this.config;
    },

    properties: function (item) {
      return item && item.properties && typeof item.properties === 'object'
        ? item.properties
        : {};
    },

    itemKey: function (item) {
      return String(item && item.key ? item.key : '');
    },

    isGift: function (item) {
      return String(this.properties(item)[ROLE_KEY] || '') === ROLE_VALUE;
    },

    isSelectedGift: function (item) {
      return Boolean(
        this.config &&
        this.config.variantId &&
        item &&
        this.isGift(item) &&
        toNumber(item.variant_id || item.id) === this.config.variantId
      );
    },

    regularItems: function (cart) {
      var self = this;
      return cart && Array.isArray(cart.items)
        ? cart.items.filter(function (item) { return !self.isGift(item); })
        : [];
    },

    regularQuantity: function (cart) {
      return this.regularItems(cart).reduce(function (total, item) {
        return total + Math.max(0, toNumber(item.quantity));
      }, 0);
    },

    regularSubtotal: function (cart) {
      return this.regularItems(cart).reduce(function (total, item) {
        return total + Math.max(0, toNumber(item.final_line_price));
      }, 0);
    },

    shouldHaveGift: function (cart) {
      return Boolean(
        this.config &&
        this.config.enabled &&
        this.config.variantId &&
        this.config.minimumCents > 0 &&
        this.regularQuantity(cart) > 0 &&
        this.regularSubtotal(cart) >= this.config.minimumCents
      );
    },

    getSectionsToRender: function () {
      var cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
        return cartItems.getSectionsToRender() || [];
      }

      var miniCart = document.querySelector('mini-cart');
      if (miniCart && typeof miniCart.getSectionsToRender === 'function') {
        return miniCart.getSectionsToRender() || [];
      }

      return [];
    },

    getSectionNames: function () {
      var names = [];
      this.getSectionsToRender().forEach(function (section) {
        var name = section.section || section.id;
        if (name && names.indexOf(name) === -1) names.push(name);
      });
      return names;
    },

    getSectionIds: function () {
      var ids = [];
      this.getSectionsToRender().forEach(function (section) {
        var id = section.id || section.section;
        if (id && ids.indexOf(id) === -1) ids.push(id);
      });
      return ids;
    },

    fetchCart: function () {
      return fetch(route('cart'), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      }).then(function (response) {
        if (!response.ok) throw new Error('Não foi possível consultar o carrinho.');
        return response.json();
      });
    },

    waitForCart: function (predicate, attempts, delay) {
      var self = this;
      var remaining = Math.max(0, toNumber(attempts));
      var wait = Math.max(0, toNumber(delay));

      return new Promise(function (resolve) {
        function run() {
          self.fetchCart()
            .then(function (cart) {
              if (!predicate || predicate(cart) || remaining <= 0) {
                resolve(cart);
                return;
              }

              remaining -= 1;
              window.setTimeout(run, wait);
            })
            .catch(function () {
              if (remaining <= 0) {
                resolve(null);
                return;
              }

              remaining -= 1;
              window.setTimeout(run, wait);
            });
        }

        run();
      });
    },

    expectedVariantFromResponse: function (response) {
      if (!response) return 0;
      if (typeof response.item_count !== 'undefined' || response.token) return 0;
      if (response.variant_id || response.id) {
        return Math.max(0, toNumber(response.variant_id || response.id));
      }
      if (Array.isArray(response.items) && response.items.length) {
        return Math.max(0, toNumber(response.items[0].variant_id || response.items[0].id));
      }
      return 0;
    },

    setBusy: function (busy) {
      document.documentElement.classList.toggle('beout-gift-is-syncing', Boolean(busy));
    },

    postRenderAdjustments: function () {
      document.querySelectorAll('[data-beout-automatic-gift="true"]').forEach(function (row) {
        row.querySelectorAll('cart-remove-button, quantity-input, .delete-product, [name="plus"], [name="minus"]').forEach(function (control) {
          control.hidden = true;
          control.setAttribute('aria-hidden', 'true');
          if ('disabled' in control) control.disabled = true;
        });
      });

      if (window.medidorCartSync && typeof window.medidorCartSync.postRenderAdjustments === 'function') {
        window.medidorCartSync.postRenderAdjustments();
      }

      if (window.SEInitCartDailyCountdown) {
        window.SEInitCartDailyCountdown(document);
      }
    },

    applyEmptyState: function (cart) {
      if (!cart || typeof cart.item_count === 'undefined') return;
      var isEmpty = toNumber(cart.item_count) === 0;

      document.querySelectorAll('cart-items').forEach(function (element) {
        element.classList.toggle('is-empty', isEmpty);
      });
      document.querySelectorAll('.mini-cart').forEach(function (element) {
        element.classList.toggle('is-empty', isEmpty);
      });

      var footer = document.getElementById('main-cart-footer');
      if (footer) footer.classList.toggle('is-empty', isEmpty);
    },

    applySections: function (state) {
      if (!state || !state.sections) return false;
      var rendered = false;

      this.getSectionsToRender().forEach(function (section) {
        var element = document.getElementById(section.id);
        var sectionHtml = state.sections[section.section] || state.sections[section.id];
        if (!element || !sectionHtml) return;

        var parsedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
        var source = parsedDocument.querySelector(section.selector) || parsedDocument.querySelector('.shopify-section') || parsedDocument.body;
        var target = element.querySelector(section.selector) || element;

        if (target && source) {
          target.innerHTML = source.innerHTML;
          rendered = true;
        }
      });

      this.applyEmptyState(state);
      this.postRenderAdjustments();
      return rendered;
    },

    removeStaleGiftNodes: function (cart) {
      var self = this;
      var activeKeys = new Set(
        cart && Array.isArray(cart.items)
          ? cart.items.map(function (item) { return self.itemKey(item); }).filter(Boolean)
          : []
      );

      document.querySelectorAll('[data-beout-automatic-gift="true"]').forEach(function (node) {
        var row = node.matches('li, tr') ? node : node.closest('li, tr');
        if (!row) return;

        var key = row.getAttribute('data-cart-item-key') || row.getAttribute('bss-b2b-cart-item-key') || '';
        if (!key || !activeKeys.has(key)) row.remove();
      });
    },


    refreshMiniCartFromSection: function () {
      var self = this;
      var miniCart = document.getElementById('mini-cart');
      if (!miniCart) return Promise.resolve(null);

      if (this.miniCartRefreshPromise) return this.miniCartRefreshPromise;

      var mainBefore = miniCart.querySelector('.mini-cart__main');
      var previousScrollTop = mainBefore ? mainBefore.scrollTop : 0;
      var baseUrl = miniCart.getAttribute('data-url') || (rootUrl() + '?section_id=mini-cart');
      var separator = baseUrl.indexOf('?') > -1 ? '&' : '?';
      var url = baseUrl + separator + '_beout_gift=' + Date.now();

      this.miniCartRefreshPromise = fetch(url, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'text/html' }
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Não foi possível atualizar o carrinho lateral.');
          return response.text();
        })
        .then(function (html) {
          var parsedDocument = new DOMParser().parseFromString(html, 'text/html');
          var source = parsedDocument.querySelector('.shopify-section') || parsedDocument.body;
          if (!source) return null;

          miniCart.innerHTML = source.innerHTML;
          self.postRenderAdjustments();

          window.requestAnimationFrame(function () {
            var mainAfter = miniCart.querySelector('.mini-cart__main');
            if (mainAfter && previousScrollTop > 0) mainAfter.scrollTop = previousScrollTop;
          });

          return miniCart;
        })
        .catch(function (error) {
          self.logErrorOnce(error);
          return null;
        })
        .finally(function () {
          self.miniCartRefreshPromise = null;
        });

      return this.miniCartRefreshPromise;
    },

    visibleGiftRows: function () {
      return Array.prototype.slice.call(
        document.querySelectorAll('#mini-cart [data-beout-automatic-gift="true"]')
      );
    },

    miniCartLineCount: function () {
      return document.querySelectorAll(
        '#mini-cart #main-cart-items > cart-items > .mini-cart__navigation > li[data-cart-item-key], ' +
        '#mini-cart #main-cart-items > cart-items > .mini-cart__navigation > li[bss-b2b-cart-item-key]'
      ).length;
    },

    miniCartNeedsRefresh: function (cart) {
      var self = this;
      if (!document.getElementById('mini-cart') || !cart || !Array.isArray(cart.items)) return false;

      var cartGiftKeys = cart.items
        .filter(function (item) { return self.isGift(item); })
        .map(function (item) { return self.itemKey(item); })
        .filter(Boolean);

      var visibleGiftKeys = this.visibleGiftRows()
        .map(function (row) {
          return row.getAttribute('data-cart-item-key') || row.getAttribute('bss-b2b-cart-item-key') || '';
        })
        .filter(Boolean);

      if (cartGiftKeys.length !== visibleGiftKeys.length) return true;
      if (cartGiftKeys.some(function (key) { return visibleGiftKeys.indexOf(key) === -1; })) return true;

      return this.miniCartLineCount() !== cart.items.length;
    },

    ensureMiniCartMatches: function (cart, force) {
      if (!document.getElementById('mini-cart')) return Promise.resolve(null);
      if (!force && !this.miniCartNeedsRefresh(cart)) return Promise.resolve(null);
      return this.refreshMiniCartFromSection();
    },

    buildProperties: function () {
      var properties = {};
      properties[ROLE_KEY] = ROLE_VALUE;
      properties[VARIANT_KEY] = String(this.config.variantId);
      properties[BADGE_KEY] = this.config.badgeText;
      properties[SOURCE_KEY] = SOURCE_VALUE;
      return properties;
    },

    addGift: function () {
      var formData = new FormData();
      var properties = this.buildProperties();

      formData.append('id', String(this.config.variantId));
      formData.append('quantity', '1');

      Object.keys(properties).forEach(function (key) {
        formData.append('properties[' + key + ']', properties[key]);
      });

      var options = typeof window.fetchConfig === 'function'
        ? window.fetchConfig('javascript')
        : { method: 'POST', headers: { Accept: 'application/json' } };

      options.headers = options.headers || {};
      options.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete options.headers['Content-Type'];
      options.body = formData;

      return fetchJson(route('add'), options);
    },

    updateLines: function (updates) {
      var options = typeof window.fetchConfig === 'function'
        ? window.fetchConfig()
        : {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            }
          };

      options.body = JSON.stringify({
        updates: updates,
        sections: this.getSectionNames(),
        sections_url: window.location.pathname
      });

      return fetchJson(route('update'), options);
    },

    renderChangedState: function (state) {
      var miniCart = document.querySelector('mini-cart');
      var pageCart = document.querySelector('#main-cart-items[data-id]');

      if (!pageCart && miniCart && typeof miniCart.renderContents === 'function' && state && state.sections) {
        this.isRenderingGiftChange = true;
        try {
          miniCart.renderContents(state);
        } finally {
          this.isRenderingGiftChange = false;
        }
        this.applyEmptyState(state);
        this.postRenderAdjustments();
        return true;
      }

      return this.applySections(state);
    },

    refreshSections: function () {
      var sectionNames = this.getSectionNames();
      if (!sectionNames.length) return Promise.resolve(null);

      var options = typeof window.fetchConfig === 'function'
        ? window.fetchConfig()
        : {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            }
          };

      options.body = JSON.stringify({
        updates: {},
        sections: sectionNames,
        sections_url: window.location.pathname
      });

      return fetchJson(route('update'), options).then(function (state) {
        manager.renderChangedState(state);
        return state;
      });
    },

    buildUpdates: function (cart, shouldHave) {
      var self = this;
      var gifts = (cart && Array.isArray(cart.items) ? cart.items : []).filter(function (item) {
        return self.isGift(item);
      });
      var selected = gifts.filter(function (item) {
        return self.isSelectedGift(item);
      });
      var updates = {};

      gifts.forEach(function (item) {
        if (!self.isSelectedGift(item)) {
          var oldKey = self.itemKey(item);
          if (oldKey) updates[oldKey] = 0;
        }
      });

      if (!shouldHave) {
        selected.forEach(function (item) {
          var key = self.itemKey(item);
          if (key) updates[key] = 0;
        });
        return updates;
      }

      if (selected.length) {
        var keeperKey = self.itemKey(selected[0]);
        if (keeperKey && toNumber(selected[0].quantity) !== 1) {
          updates[keeperKey] = 1;
        }

        selected.slice(1).forEach(function (item) {
          var key = self.itemKey(item);
          if (key) updates[key] = 0;
        });
      }

      return updates;
    },

    reconcile: function (cart, pass) {
      var self = this;
      var currentPass = toNumber(pass);
      if (!cart || !Array.isArray(cart.items) || currentPass >= MAX_PASSES) {
        return Promise.resolve(cart);
      }

      var shouldHave = this.shouldHaveGift(cart);
      var updates = this.buildUpdates(cart, shouldHave);

      if (Object.keys(updates).length) {
        return this.updateLines(updates)
          .then(function (nextState) {
            self.lastCart = nextState;
            self.renderChangedState(nextState);
            self.removeStaleGiftNodes(nextState);
            return self.ensureMiniCartMatches(nextState, true).then(function () {
              return self.reconcile(nextState, currentPass + 1);
            });
          });
      }

      var hasSelectedGift = cart.items.some(function (item) {
        return self.isSelectedGift(item);
      });

      if (!shouldHave || hasSelectedGift) {
        this.applyEmptyState(cart);
        this.postRenderAdjustments();
        return this.ensureMiniCartMatches(cart, false).then(function () {
          return cart;
        });
      }

      return this.addGift()
        .then(function () {
          return self.waitForCart(function (nextCart) {
            return Boolean(nextCart && Array.isArray(nextCart.items) && nextCart.items.some(function (item) {
              return self.isSelectedGift(item);
            }));
          }, 6, 120);
        })
        .then(function (cartWithGift) {
          if (!cartWithGift) return self.fetchCart();
          self.lastCart = cartWithGift;
          return self.refreshSections().then(function () {
            return self.ensureMiniCartMatches(cartWithGift, true);
          }).then(function () {
            return cartWithGift;
          });
        })
        .then(function (freshCart) {
          self.lastCart = freshCart;
          return self.reconcile(freshCart, currentPass + 1);
        });
    },

    logErrorOnce: function (error) {
      var status = toNumber(error && error.status);
      var message = String(error && error.message ? error.message : error || 'Erro desconhecido');
      var signature = status + ':' + message;
      if (signature === this.lastErrorSignature) return;
      this.lastErrorSignature = signature;
      console.error('[Store Experts][Brinde] Falha na sincronização.', error);
    },

    syncFromState: function (state) {
      var self = this;
      var cart = parseCartState(state);
      this.refreshConfig();

      if (!this.config || !cart || !Array.isArray(cart.items)) {
        return Promise.resolve(cart);
      }

      if (window.medidorCartSync && window.medidorCartSync.isSyncing) {
        this.queueSync(null, 120);
        return Promise.resolve(cart);
      }

      if (this.isSyncing) {
        this.syncRequested = true;
        this.lastCart = cart;
        return this.currentPromise || Promise.resolve(cart);
      }

      this.isSyncing = true;
      this.syncRequested = false;
      this.lastCart = cart;
      this.setBusy(true);

      this.currentPromise = this.reconcile(cart, 0)
        .then(function (finalCart) {
          if (finalCart && Array.isArray(finalCart.items)) self.lastCart = finalCart;
          self.lastErrorSignature = '';
          return finalCart;
        })
        .catch(function (error) {
          self.logErrorOnce(error);
          return self.lastCart;
        })
        .finally(function () {
          self.isSyncing = false;
          self.currentPromise = null;
          self.setBusy(false);
          self.postRenderAdjustments();

          if (self.syncRequested) {
            self.syncRequested = false;
            self.syncAfterAdd(0);
          }
        });

      return this.currentPromise;
    },

    queueSync: function (state, delay) {
      var self = this;
      if (this.pendingTimer) window.clearTimeout(this.pendingTimer);

      this.pendingTimer = window.setTimeout(function () {
        self.pendingTimer = null;
        var parsed = parseCartState(state);
        if (parsed && Array.isArray(parsed.items)) {
          self.syncFromState(parsed);
        } else {
          self.syncAfterAdd(0);
        }
      }, typeof delay === 'number' ? delay : 80);
    },

    syncAfterAdd: function (delay, expectedVariantId) {
      var self = this;
      var expected = Math.max(0, toNumber(expectedVariantId));
      if (expected) this.pendingExpectedVariantId = expected;
      if (this.pendingTimer) window.clearTimeout(this.pendingTimer);

      this.pendingTimer = window.setTimeout(function () {
        self.pendingTimer = null;
        var expectedToFind = self.pendingExpectedVariantId;
        self.pendingExpectedVariantId = 0;

        self.waitForCart(function (cart) {
          if (!expectedToFind) return true;
          return Boolean(cart && Array.isArray(cart.items) && cart.items.some(function (item) {
            return toNumber(item.variant_id || item.id) === expectedToFind;
          }));
        }, expectedToFind ? 6 : 0, expectedToFind ? 180 : 0)
          .then(function (cart) {
            if (!cart) return null;
            return self.syncFromState(cart);
          })
          .catch(function (error) { self.logErrorOnce(error); });
      }, typeof delay === 'number' ? delay : 80);
    }
  };

  window.beoutGiftCartSync = manager;

  function initialize() {
    manager.refreshConfig();
    manager.postRenderAdjustments();
    manager.syncAfterAdd(0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('cart:updated', function (event) {
    var cart = event && event.detail ? event.detail.cart : null;
    manager.queueSync(cart, 0);
  });

  document.addEventListener('ajaxProduct:added', function (event) {
    var response = event && event.detail ? event.detail.product : null;
    manager.syncAfterAdd(0, manager.expectedVariantFromResponse(response));
  });

  document.addEventListener('cartdrawer:opened', function () {
    manager.postRenderAdjustments();
    manager.syncAfterAdd(0);
  });

  document.addEventListener('shopify:section:load', function () {
    manager.postRenderAdjustments();
    manager.syncAfterAdd(0);
  });


  if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event || event.source !== 'product-form') return;
      manager.syncAfterAdd(0, event.productVariantId);
    });
  } else if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event || event.source !== 'product-form') return;
      manager.syncAfterAdd(0, event.productVariantId);
    });
  }

  window.addEventListener('pageshow', function () {
    manager.syncAfterAdd(0);
  });
})();
